import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import UndoIcon from "@mui/icons-material/Undo";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from "recharts";
import { useAuth } from "../../../contexts/AuthContext";
import { useStickyTab } from "../../../utils/uiPrefs";

type BrandSummary = {
  brand_id: number;
  brand_name: string;
  produced: number;
  shipped: number;
  corrected: number;
  current: number;
};

type Shipment = {
  id: number;
  brand_id: number | null;
  brand_name: string | null;
  qty: number;
  note: string | null;
  created_by: string;
  created_at: string;
};

type Correction = {
  id: number;
  brand_id: number | null;
  brand_name: string | null;
  qty: number;
  note: string | null;
  created_by: string;
  created_at: string;
};

type Brand = { id: number; name: string; is_active: boolean };

type SOLine = { brand_id: number | null; brand_name: string; quantity: number };
type SalesOrder = { id: number; status: string; name: string; lines?: SOLine[] };

const COLORS = [
  "#4e79a7", "#f28e2b", "#e15759", "#76b7b2", "#59a14f",
  "#edc948", "#b07aa1", "#ff9da7", "#9c755f", "#bab0ac",
];

const InventoryForecastPage: React.FC = () => {
  const { token, hasAdminAccess } = useAuth();
  const api = process.env.REACT_APP_API_URL || "";
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const [tab, setTab] = useStickyTab("inventory", 3);

  // SO coverage state — pending qty per brand_id across open (draft/confirmed) SOs
  const [pendingByBrand, setPendingByBrand] = useState<Map<number, number>>(new Map());

  // Data
  const [summary, setSummary] = useState<BrandSummary[]>([]);
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [corrections, setCorrections] = useState<Correction[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);

  // Shipment form
  const [shipBrandId, setShipBrandId] = useState<number | "">("");
  const [shipQty, setShipQty] = useState("");
  const [shipNote, setShipNote] = useState("");
  const [shipSaving, setShipSaving] = useState(false);

  // Correction form
  const [corrBrandId, setCorrBrandId] = useState<number | "">("");
  const [corrQty, setCorrQty] = useState("");
  const [corrNote, setCorrNote] = useState("");
  const [corrSaving, setCorrSaving] = useState(false);

  // Undo dialog
  const [undoTarget, setUndoTarget] = useState<Correction | null>(null);

  const [snack, setSnack] = useState<{ open: boolean; msg: string; severity: "success" | "error" }>
    ({ open: false, msg: "", severity: "success" });

  const showSnack = (msg: string, severity: "success" | "error") =>
    setSnack({ open: true, msg, severity });

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, shipRes, corrRes, brandRes, soListRes] = await Promise.all([
        fetch(`${api}/inventory/summary`, { headers }),
        fetch(`${api}/inventory/shipments`, { headers }),
        fetch(`${api}/inventory/corrections`, { headers }),
        fetch(`${api}/brands`, { headers }),
        fetch(`${api}/sales-orders`, { headers }),
      ]);
      if (sumRes.ok) setSummary(await sumRes.json());
      if (shipRes.ok) setShipments(await shipRes.json());
      if (corrRes.ok) setCorrections(await corrRes.json());
      if (brandRes.ok) setBrands((await brandRes.json() as Brand[]).filter(b => b.is_active));

      // Fetch line details for open SOs (draft/confirmed) to build brand-level pending totals
      if (soListRes.ok) {
        const allSOs: SalesOrder[] = await soListRes.json();
        const openSOs = allSOs.filter(so => so.status === "draft" || so.status === "confirmed");
        const details = await Promise.all(
          openSOs.map(so => fetch(`${api}/sales-orders/${so.id}`, { headers }).then(r => r.ok ? r.json() : null))
        );
        const pending = new Map<number, number>();
        for (const so of details) {
          if (!so?.lines) continue;
          for (const line of so.lines as SOLine[]) {
            if (line.brand_id == null) continue;
            pending.set(line.brand_id, (pending.get(line.brand_id) ?? 0) + line.quantity);
          }
        }
        setPendingByBrand(pending);
      }
    } catch {
      showSnack("Failed to load inventory data", "error");
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { loadAll(); }, [loadAll]);

  const chartData = useMemo(() =>
    summary.map((s, i) => ({
      name: s.brand_name,
      current: s.current,
      color: COLORS[i % COLORS.length],
    })),
    [summary]
  );

  const totalInventory = useMemo(() => summary.reduce((acc, s) => acc + s.current, 0), [summary]);

  // Per-brand coverage: current stock vs pending SO demand
  const coverageData = useMemo(() =>
    summary.map(s => {
      const pending = pendingByBrand.get(s.brand_id) ?? 0;
      const afterFulfillment = s.current - pending;
      return {
        name: s.brand_name,
        inStock: s.current,
        pending,
        afterFulfillment,
      };
    }),
    [summary, pendingByBrand]
  );

  // ---- Shipment submit ----
  const handleAddShipment = async () => {
    const qty = parseInt(shipQty);
    if (!shipBrandId || qty <= 0) return;
    setShipSaving(true);
    try {
      const brand = brands.find(b => b.id === shipBrandId);
      const res = await fetch(`${api}/inventory/shipments`, {
        method: "POST",
        headers,
        body: JSON.stringify({ brand_id: shipBrandId, brand_name: brand?.name, qty, note: shipNote.trim() || null }),
      });
      if (!res.ok) throw new Error();
      setShipBrandId(""); setShipQty(""); setShipNote("");
      showSnack("Shipment recorded", "success");
      await loadAll();
    } catch {
      showSnack("Failed to add shipment", "error");
    } finally {
      setShipSaving(false);
    }
  };

  const handleDeleteShipment = async (id: number) => {
    try {
      await fetch(`${api}/inventory/shipments/${id}`, { method: "DELETE", headers });
      await loadAll();
    } catch {
      showSnack("Failed to delete shipment", "error");
    }
  };

  // ---- Correction submit ----
  const handleAddCorrection = async () => {
    const qty = parseInt(corrQty);
    if (!corrBrandId || qty === 0) return;
    setCorrSaving(true);
    try {
      const brand = brands.find(b => b.id === corrBrandId);
      const res = await fetch(`${api}/inventory/corrections`, {
        method: "POST",
        headers,
        body: JSON.stringify({ brand_id: corrBrandId, brand_name: brand?.name, qty, note: corrNote.trim() || null }),
      });
      if (!res.ok) throw new Error();
      setCorrBrandId(""); setCorrQty(""); setCorrNote("");
      showSnack("Correction posted", "success");
      await loadAll();
    } catch {
      showSnack("Failed to add correction", "error");
    } finally {
      setCorrSaving(false);
    }
  };

  const handleUndoCorrection = async () => {
    if (!undoTarget) return;
    try {
      await fetch(`${api}/inventory/corrections/${undoTarget.id}`, { method: "DELETE", headers });
      setUndoTarget(null);
      showSnack("Correction removed", "success");
      await loadAll();
    } catch {
      showSnack("Failed to remove correction", "error");
    }
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: { xs: 0, sm: 1 } }}>
      <Typography variant="h5" fontWeight={700} mb={1}>Inventory</Typography>
      <Typography variant="body2" color="text.secondary" mb={{ xs: 2, sm: 3 }}>
        Production from manufacturing orders · {brands.length} active brands · {totalInventory} total rolls in stock
      </Typography>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mb: { xs: 2, sm: 3 } }}
      >
        <Tab label="Inventory" />
        <Tab label="Shipments" />
        <Tab label="SO Coverage" />
      </Tabs>

      {/* ---- Inventory tab ---- */}
      {tab === 0 && (
        <>
          {summary.length === 0 ? (
            <Typography color="text.secondary">No inventory data yet. Create brands and submit MO entries to get started.</Typography>
          ) : (
            <>
              <Paper variant="outlined" sx={{ p: { xs: 1, sm: 2 }, mb: 3, borderRadius: 2 }}>
                <Box sx={{ width: "100%", height: { xs: 220, sm: 280 } }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 20, right: 8, bottom: 5, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                      <RechartsTooltip formatter={(v) => [`${v} rolls`, "In Stock"]} />
                      <Bar dataKey="current" radius={[4, 4, 0, 0]}>
                        {chartData.map((entry, i) => (
                          <Cell key={i} fill={entry.color} />
                        ))}
                        <LabelList dataKey="current" position="top" style={{ fontSize: 11 }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>

              {/* Brand breakdown table */}
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Brand</TableCell>
                      <TableCell align="right">Produced</TableCell>
                      <TableCell align="right">Shipped</TableCell>
                      <TableCell align="right">Corrections</TableCell>
                      <TableCell align="right">In Stock</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {summary.map((s, i) => (
                      <TableRow key={s.brand_id}>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: COLORS[i % COLORS.length] }} />
                            {s.brand_name}
                          </Box>
                        </TableCell>
                        <TableCell align="right">{s.produced}</TableCell>
                        <TableCell align="right">{s.shipped}</TableCell>
                        <TableCell align="right">
                          <Typography color={s.corrected >= 0 ? "success.main" : "error.main"} component="span">
                            {s.corrected >= 0 ? `+${s.corrected}` : s.corrected}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{s.current}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {/* Correction form */}
          <Typography variant="subtitle1" fontWeight={600} mb={1}>Post Correction</Typography>
          <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, borderRadius: 2 }}>
            <Box display="flex" gap={{ xs: 1, sm: 2 }} flexWrap="wrap" alignItems="flex-start">
              <TextField
                select label="Brand" size="small" sx={{ minWidth: { xs: "100%", sm: 160 } }}
                value={corrBrandId} onChange={e => setCorrBrandId(Number(e.target.value))}
              >
                <MenuItem value="">— Brand —</MenuItem>
                {brands.map(b => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
              </TextField>
              <TextField
                label="Qty (±)" type="number" size="small" sx={{ width: { xs: "100%", sm: 110 } }}
                value={corrQty} onChange={e => setCorrQty(e.target.value)}
                helperText="Negative to remove"
              />
              <TextField
                label="Reason" size="small" sx={{ flex: 1, minWidth: { xs: "100%", sm: 180 } }}
                value={corrNote} onChange={e => setCorrNote(e.target.value)}
              />
              <Button
                variant="contained" onClick={handleAddCorrection}
                disabled={corrSaving || !corrBrandId || !corrQty || parseInt(corrQty) === 0}
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                {corrSaving ? <CircularProgress size={18} /> : "Post"}
              </Button>
            </Box>
          </Paper>

          {/* Corrections history */}
          {corrections.length > 0 && (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>Brand</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>By</TableCell>
                  {hasAdminAccess && <TableCell />}
                </TableRow>
              </TableHead>
              <TableBody>
                {corrections.map(c => (
                  <TableRow key={c.id}>
                    <TableCell>{new Date(c.created_at).toLocaleString()}</TableCell>
                    <TableCell>{c.brand_name ?? "—"}</TableCell>
                    <TableCell align="right">
                      <Typography color={c.qty >= 0 ? "success.main" : "error.main"} component="span">
                        {c.qty >= 0 ? `+${c.qty}` : c.qty}
                      </Typography>
                    </TableCell>
                    <TableCell>{c.note ?? "—"}</TableCell>
                    <TableCell>{c.created_by}</TableCell>
                    {hasAdminAccess && (
                      <TableCell>
                        <Tooltip title="Undo correction">
                          <IconButton size="small" onClick={() => setUndoTarget(c)}>
                            <UndoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* ---- Shipments tab ---- */}
      {tab === 1 && (
        <>
          <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, mb: 3, borderRadius: 2 }}>
            <Box display="flex" gap={{ xs: 1, sm: 2 }} flexWrap="wrap" alignItems="flex-start">
              <TextField
                select label="Brand" size="small" sx={{ minWidth: { xs: "100%", sm: 160 } }}
                value={shipBrandId} onChange={e => setShipBrandId(Number(e.target.value))}
              >
                <MenuItem value="">— Brand —</MenuItem>
                {brands.map(b => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
              </TextField>
              <TextField
                label="Qty" type="number" size="small" sx={{ width: { xs: "100%", sm: 110 } }}
                value={shipQty} onChange={e => setShipQty(e.target.value)}
                slotProps={{ htmlInput: { min: 1 } }}
              />
              <TextField
                label="Note" size="small" sx={{ flex: 1, minWidth: { xs: "100%", sm: 180 } }}
                value={shipNote} onChange={e => setShipNote(e.target.value)}
              />
              <Button
                variant="contained" onClick={handleAddShipment}
                disabled={shipSaving || !shipBrandId || parseInt(shipQty) <= 0}
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                {shipSaving ? <CircularProgress size={18} /> : "Add Shipment"}
              </Button>
            </Box>
          </Paper>

          {shipments.length === 0 ? (
            <Typography color="text.secondary">No shipments recorded yet.</Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>Brand</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell>Note</TableCell>
                  <TableCell>By</TableCell>
                  {hasAdminAccess && <TableCell />}
                </TableRow>
              </TableHead>
              <TableBody>
                {shipments.map(s => (
                  <TableRow key={s.id}>
                    <TableCell>{new Date(s.created_at).toLocaleString()}</TableCell>
                    <TableCell>{s.brand_name ?? "—"}</TableCell>
                    <TableCell align="right">{s.qty}</TableCell>
                    <TableCell>{s.note ?? "—"}</TableCell>
                    <TableCell>{s.created_by}</TableCell>
                    {hasAdminAccess && (
                      <TableCell>
                        <Tooltip title="Delete shipment">
                          <IconButton size="small" color="error" onClick={() => handleDeleteShipment(s.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* ---- SO Coverage tab ---- */}
      {tab === 2 && (
        <>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Current stock vs. quantity committed in open sales orders (draft &amp; confirmed).
          </Typography>

          {coverageData.length === 0 ? (
            <Typography color="text.secondary">No inventory data yet.</Typography>
          ) : (
            <>
              <Paper variant="outlined" sx={{ p: { xs: 1, sm: 2 }, mb: 3, borderRadius: 2 }}>
                <Box sx={{ width: "100%", height: { xs: 240, sm: 300 } }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={coverageData} margin={{ top: 20, right: 8, bottom: 4, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                      <RechartsTooltip formatter={(v, name) => [
                        `${v} rolls`,
                        name === "inStock" ? "In Stock" : name === "pending" ? "Pending Orders" : "After Fulfillment",
                      ]} />
                      <Legend formatter={(v) =>
                        v === "inStock" ? "In Stock" : v === "pending" ? "Pending Orders" : "After Fulfillment"
                      } />
                      <Bar dataKey="inStock" name="inStock" fill="#4e79a7" radius={[4, 4, 0, 0]}>
                        <LabelList dataKey="inStock" position="top" style={{ fontSize: 10 }} />
                      </Bar>
                      <Bar dataKey="pending" name="pending" fill="#f28e2b" radius={[4, 4, 0, 0]}>
                        <LabelList dataKey="pending" position="top" style={{ fontSize: 10 }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>

              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Brand</TableCell>
                      <TableCell align="right">In Stock</TableCell>
                      <TableCell align="right">Pending Orders</TableCell>
                      <TableCell align="right">After Fulfillment</TableCell>
                      <TableCell align="right">Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {coverageData.map((row) => {
                      const shortfall = row.afterFulfillment < 0;
                      const exact = row.afterFulfillment === 0 && row.pending > 0;
                      return (
                        <TableRow key={row.name}>
                          <TableCell>{row.name}</TableCell>
                          <TableCell align="right">{row.inStock}</TableCell>
                          <TableCell align="right">{row.pending || "—"}</TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 600, color: shortfall ? "error.main" : exact ? "warning.main" : "success.main" }}
                          >
                            {row.pending > 0 ? row.afterFulfillment : "—"}
                          </TableCell>
                          <TableCell align="right">
                            {row.pending === 0
                              ? <Typography variant="caption" color="text.secondary">No open orders</Typography>
                              : shortfall
                              ? <Typography variant="caption" color="error.main">⚠ Shortfall {Math.abs(row.afterFulfillment)}</Typography>
                              : exact
                              ? <Typography variant="caption" color="warning.main">Exactly covered</Typography>
                              : <Typography variant="caption" color="success.main">✓ Covered</Typography>
                            }
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </>
      )}

      {/* Undo correction dialog */}
      <Dialog open={!!undoTarget} onClose={() => setUndoTarget(null)} maxWidth="xs">
        <DialogTitle>Undo Correction</DialogTitle>
        <DialogContent>
          <Typography>
            Remove the {undoTarget?.qty && undoTarget.qty >= 0 ? `+${undoTarget.qty}` : undoTarget?.qty} correction
            for <strong>{undoTarget?.brand_name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUndoTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleUndoCorrection}>Remove</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default InventoryForecastPage;
